import type { Node, Edge } from 'reactflow';

// Import JSON template files
// Full Stack
import mernStack from './templates/fullstack/mern-stack.json';
import nextjsStack from './templates/fullstack/nextjs-stack.json';
import t3Stack from './templates/fullstack/t3-stack.json';
import djangoStack from './templates/fullstack/django-stack.json';
import ecommercePlatform from './templates/fullstack/ecommerce-platform.json';
import blockchainDapp from './templates/fullstack/blockchain-dapp.json';

// Frontend
import reactSpa from './templates/frontend/react-spa.json';
import vueSpa from './templates/frontend/vue-spa.json';
import progressiveWebApp from './templates/frontend/progressive-web-app.json';

// Backend
import fastapi from './templates/backend/fastapi.json';
import expressApi from './templates/backend/express-api.json';
import graphqlApiGateway from './templates/backend/graphql-api-gateway.json';
import gamingBackend from './templates/backend/gaming-backend.json';

// DevOps
import cicdPipeline from './templates/devops/cicd-pipeline.json';
import awsServerless from './templates/devops/aws-serverless.json';
import monitoringStack from './templates/devops/monitoring-stack.json';
import kubernetesCluster from './templates/devops/kubernetes-cluster.json';
import machineLearningPipeline from './templates/devops/machine-learning-pipeline.json';
import hybridCloudArchitecture from './templates/devops/hybrid-cloud-architecture.json';

// Microservices
import basicMicroservices from './templates/microservices/basic-microservices.json';
import eventDrivenArchitecture from './templates/microservices/event-driven-architecture.json';

// Homelab
import mediaServer from './templates/homelab/media-server.json';
import basicHomelab from './templates/homelab/basic-homelab.json';
import smartHomeSetup from './templates/homelab/smart-home-setup.json';

// Mobile
import reactNative from './templates/mobile/react-native.json';
import flutterApp from './templates/mobile/flutter-app.json';

/**
 * Template interface - matches the JSON structure
 * 
 * To create a new template:
 * 1. Create a diagram in the editor
 * 2. Export it as JSON using the export feature
 * 3. Add metadata fields (id, name, description, category, tags) to the JSON
 * 4. Save the file in the appropriate category folder under src/data/templates/
 * 5. Import and add it to the TEMPLATES array below
 */
export interface Template {
  id: string;
  name: string;
  description: string;
  category: TemplateCategory;
  tags: string[];
  thumbnail?: string;
  nodes: Node[];
  edges: Edge[];
}

/**
 * Raw template data from JSON (before type casting)
 */
interface RawTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  tags: string[];
  thumbnail?: string;
  nodes: Array<{
    id: string;
    type: string;
    position: { x: number; y: number };
    data: {
      label: string;
      icon: string;
      hex: string;
      description?: string;
    };
  }>;
  edges: Array<{
    id: string;
    source: string;
    target: string;
    sourceHandle?: string;
    targetHandle?: string;
  }>;
}

export type TemplateCategory = 
  | 'fullstack'
  | 'frontend'
  | 'backend'
  | 'devops'
  | 'microservices'
  | 'homelab'
  | 'mobile';

export const TEMPLATE_CATEGORIES: { id: TemplateCategory; label: string; description: string }[] = [
  { id: 'fullstack', label: 'Full Stack', description: 'Complete web application stacks' },
  { id: 'frontend', label: 'Frontend', description: 'Client-side architectures' },
  { id: 'backend', label: 'Backend', description: 'Server-side and API architectures' },
  { id: 'devops', label: 'DevOps', description: 'CI/CD and deployment pipelines' },
  { id: 'microservices', label: 'Microservices', description: 'Distributed system architectures' },
  { id: 'homelab', label: 'Homelab', description: 'Self-hosted infrastructure setups' },
  { id: 'mobile', label: 'Mobile', description: 'Mobile app architectures' },
];

/**
 * Converts a raw JSON template to the proper Template type
 * Adds default edge styling for consistent appearance
 */
function processTemplate(raw: RawTemplate): Template {
  return {
    ...raw,
    category: raw.category as TemplateCategory,
    nodes: raw.nodes as Node[],
    edges: raw.edges.map((edge) => ({
      ...edge,
      animated: false,
      style: { stroke: '#64748b', strokeWidth: 2 },
      markerEnd: { type: 'arrowclosed' as const, color: '#64748b' },
    })) as Edge[],
  };
}

// ============================================
// TEMPLATE REGISTRY
// ============================================
// Add your imported JSON templates here
// Templates are organized by category for easier management

const rawTemplates: RawTemplate[] = [
  // Full Stack (6)
  mernStack as RawTemplate,
  nextjsStack as RawTemplate,
  t3Stack as RawTemplate,
  djangoStack as RawTemplate,
  ecommercePlatform as RawTemplate,
  blockchainDapp as RawTemplate,
  
  // Frontend (3)
  reactSpa as RawTemplate,
  vueSpa as RawTemplate,
  progressiveWebApp as RawTemplate,
  
  // Backend (4)
  fastapi as RawTemplate,
  expressApi as RawTemplate,
  graphqlApiGateway as RawTemplate,
  gamingBackend as RawTemplate,
  
  // DevOps (6)
  cicdPipeline as RawTemplate,
  awsServerless as RawTemplate,
  monitoringStack as RawTemplate,
  kubernetesCluster as RawTemplate,
  machineLearningPipeline as RawTemplate,
  hybridCloudArchitecture as RawTemplate,
  
  // Microservices (2)
  basicMicroservices as RawTemplate,
  eventDrivenArchitecture as RawTemplate,
  
  // Homelab (3)
  mediaServer as RawTemplate,
  basicHomelab as RawTemplate,
  smartHomeSetup as RawTemplate,
  
  // Mobile (2)
  reactNative as RawTemplate,
  flutterApp as RawTemplate,
];

/**
 * All available templates, processed and ready to use
 */
export const TEMPLATES: Template[] = rawTemplates.map(processTemplate);

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get templates filtered by category
 */
export const getTemplatesByCategory = (category: TemplateCategory): Template[] => {
  return TEMPLATES.filter((t) => t.category === category);
};

/**
 * Get a single template by its ID
 */
export const getTemplateById = (id: string): Template | undefined => {
  return TEMPLATES.find((t) => t.id === id);
};

/**
 * Search templates by name, description, or tags
 */
export const searchTemplates = (query: string): Template[] => {
  const lowerQuery = query.toLowerCase();
  return TEMPLATES.filter(
    (t) =>
      t.name.toLowerCase().includes(lowerQuery) ||
      t.description.toLowerCase().includes(lowerQuery) ||
      t.tags.some((tag) => tag.toLowerCase().includes(lowerQuery))
  );
};
